import {
  Suspense,
  ComponentType,
  LazyExoticComponent,
  ComponentProps
} from 'react';
import SuspenseLoader from 'src/components/SuspenseLoader';

/**
 * @description A higher-order component (HOC) designed to simplify the use of
 * React's lazy-loading feature for efficient code-splitting. This component
 * automatically handles the required Suspense boundary and a consistent
 * loading state, making dynamic imports a seamless part of the application.
 *
 * @template T - Represents the component type and its associated props. This
 * ensures the Loader is type-safe and can be reused with any component.
 *
 * @param {LazyExoticComponent<T>} Component - A lazy-loaded component created
 * with `React.lazy()`.
 *
 * @returns {ComponentType<ComponentProps<T>>} A new component that handles
 * the lazy-loading process. It correctly forwards all original props and
 * gracefully manages the loading state.
 *
 * @example
 * // Usage in a route definition file
 * import Loader from 'path/to/Loader';
 * import { lazy } from 'react';
 *
 * const Dashboard = Loader(lazy(() => import('./pages/Dashboard')));
 *
 * // Then, use it as a regular component in your route:
 * <Route path="/dashboard" element={<Dashboard />} />
 */
const Loader = <T extends ComponentType<any>>(
  Component: LazyExoticComponent<T>
): ComponentType<ComponentProps<T>> => {
  const WrappedComponent = (props: ComponentProps<T>) => (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

  return WrappedComponent;
};

export default Loader;
